import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
// #21 — the ONE mapper for staff-API failures. `toastFromError` delegates to it
// rather than formatting anything itself; see the ⛔⛔ note on that function.
import { friendlyStaffError } from '../lib/friendly-error';

/** The title used when a caller names no action. Shared with `toastFromError`,
 *  which collapses the detail when it would merely restate this. */
const DEFAULT_TOAST_TITLE = 'Something went wrong';

/**
 * ToastProvider — fixed top-right notification stack. Wraps the app
 * at the root; any descendant component calls useToast() to push
 * notifications without owning state.
 *
 * Design intent: replace the silent-failure pattern that was leaving
 * operators unsure whether their action fired (Reassess returned 503,
 * UI optimistically updated but the real server state never changed,
 * operator stares at a row that "looks" pending forever). Toasts
 * surface the success path AND the failure path; the on-page
 * inline-error fallback remains for cases where the operator
 * dismisses the toast before reading it.
 *
 * Variants:
 *   success — auto-dismiss after 4s. Confirmation that an action fired.
 *   error   — sticky by default. Operator must dismiss.
 *
 *             ⛔ IT DOES NOT SURFACE THE API RESPONSE. This line used to read
 *             "surfaces the error message verbatim from the API response so
 *             they can screenshot / paste to support" — which is the prose that
 *             licensed the leak `toastFromError` shipped until 31 July 2026, and
 *             it contradicts the standing error-surfacing directive outright. A
 *             status code, a request path and a raw body never reach a toast;
 *             they go to `console`. Support gets the console, the user gets a
 *             sentence.
 *   info    — auto-dismiss after 6s. Background context (e.g.
 *             "Background refresh failed twice — checking again in 5s").
 *
 * Standard pattern. The same component can be lifted into
 * portal-subscriber when the subscriber-side error surface needs the
 * same treatment.
 */

export type ToastVariant = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  variant: ToastVariant;
  title: string;
  detail?: string;
  /** Override the default auto-dismiss timer (ms). 0 = sticky. */
  durationMs?: number;
}

interface ToastContextValue {
  push: (toast: Omit<Toast, 'id'>) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATIONS: Record<ToastVariant, number> = {
  success: 4000,
  info: 6000,
  error: 0, // sticky — operator dismisses manually
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const push = useCallback((toast: Omit<Toast, 'id'>): string => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const full: Toast = { ...toast, id };
    setToasts((cur) => [...cur, full]);
    const duration = full.durationMs ?? DEFAULT_DURATIONS[full.variant];
    if (duration > 0) {
      const timer = setTimeout(() => dismiss(id), duration);
      timersRef.current.set(id, timer);
    }
    return id;
  }, [dismiss]);

  // Clean up timers on unmount.
  useEffect(() => () => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current.clear();
  }, []);

  // Stabilise the context value. push/dismiss are already useCallback'd
  // (stable for the provider's lifetime), so this object only ever has
  // one identity. Without the memo, a fresh `{ push, dismiss }` literal
  // on every ToastProvider render gives each useToast() consumer a new
  // reference — which re-runs any effect that lists the toast handle in
  // its dependency array. ApplicationsQueue's fetch effect does exactly
  // that, so the old unstable value caused an infinite render loop on
  // the error path: fetch fails → push() error toast → ToastProvider
  // re-renders → new context value → effect re-runs → fetch fails →
  // push() → … (hangs the test runner; pegs the CPU + hammers admin-api
  // in production whenever a load errors). Memoising breaks the cycle.
  const value = useMemo(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="portal-toast-stack" aria-live="polite" aria-atomic="false">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`portal-toast portal-toast-${t.variant}`}
            role={t.variant === 'error' ? 'alert' : 'status'}
          >
            <div className="portal-toast-icon" aria-hidden="true">
              {t.variant === 'success' ? '✓' : t.variant === 'error' ? '⚠' : 'ℹ'}
            </div>
            <div className="portal-toast-body">
              <div className="portal-toast-title">{t.title}</div>
              {t.detail && <div className="portal-toast-detail">{t.detail}</div>}
            </div>
            <button
              type="button"
              className="portal-toast-close"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss notification"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a <ToastProvider>');
  }
  return ctx;
}

/**
 * Helper: takes an unknown error and returns a title + detail pair for pushing
 * into the toast stack. `title` names the ACTION that failed (the caller's
 * `fallbackTitle`); `detail` is a sentence the reader can act on.
 *
 * ⛔⛔ THIS FUNCTION WAS THE LEAK, AND IT LEAKED ON EVERY PATH — 31 July 2026.
 * It used to put the HTTP status in the title and `path\nbody` in the detail,
 * which is a flat breach of the error-surfacing standard: *"Never show the raw
 * error… A raw API/Amplify/Cognito error string or HTTP status code must never
 * reach the user."* It is also an information-disclosure issue rather than a
 * polish item — a request path and a raw backend body tell an observer more
 * about the API surface than a user-facing toast should.
 *
 * ⭐ AND THE "STRUCTURED" BRANCH HAD SILENTLY BECOME DEAD CODE, which made it
 * WORSE than it looked. It matched `/^admin-api (\d+) on (\S+): (.+)$/`, but
 * ADR-099 W6b deleted `lambdas/admin-api` and `StaffApiError` now formats
 * `staff-api {status} on {path}: {code}`. So the regex never matched, every
 * error fell to the `return { detail: err.message }` line below it, and the
 * WHOLE diagnostic string — status, path AND code — went into the toast
 * verbatim. A leak nobody had to opt into.
 *
 * ⭐ THE FIX IS DELEGATION, NOT A SECOND MAPPER. `friendlyStaffError` is already
 * the one place a staff-API failure becomes a sentence, and its contract is the
 * one that matters here: an UNMAPPED code returns a generic sentence, never the
 * raw text. Re-implementing any of that here would be two mappers for one
 * boundary, free to drift — which is the exact defect `friendly-error.ts` was
 * extracted to end (two `humaniseError` closures, both ending `return msg`).
 *
 * ⚠ THE RAW STRING IS NOT DISCARDED — it goes to `console.error` and nowhere
 * else. Diagnosis is preserved; the user-facing surface is not the log.
 */
export function toastFromError(err: unknown, fallbackTitle = DEFAULT_TOAST_TITLE): { title: string; detail?: string } {
  const raw = err instanceof Error ? err.message : String(err);
  // Diagnostic only. `friendlyStaffError` additionally logs the unmapped ones;
  // this logs every one, so a MAPPED failure is still traceable in the console.
  console.error('staff-portal: toast from API error', raw);

  const detail = friendlyStaffError(raw);
  // The generic fall-through restates the default title almost word for word
  // ("Something went wrong" / "Something went wrong. Please try again in a
  // moment."). Two lines saying one thing reads as a bug, so collapse it.
  if (fallbackTitle === DEFAULT_TOAST_TITLE && detail.startsWith(DEFAULT_TOAST_TITLE)) {
    return { title: fallbackTitle, detail: 'Please try again in a moment.' };
  }
  return { title: fallbackTitle, detail };
}
